import { CreateTaskAssignBody } from '../../../types';
import { TaskNotification } from './addnotification';

type TaskAssignedValue<T> = {
  value: T;
  showError: boolean;
  missing?: boolean;
};

export interface TaskAssignedAsset {
  id: number;
  name: string;
  code?: string;
}

export interface TaskAssigned {
  users: TaskAssignedValue<number[]>;
  // Selected user_group ids when the task is dispatched to a group instead of
  // individual mobile users. Mutually exclusive with `users` at the UI level
  // (the selector drawer disables the opposite tab when one is set).
  groups: TaskAssignedValue<number[]>;
  forms: TaskAssignedValue<number[]>;
  location: TaskAssignedValue<number>;
  locationType?: TaskAssignedValue<'NestableLocation' | 'Location'>;
  description: TaskAssignedValue<string>;
  gap: TaskAssignedValue<number>;
  subTasks: TaskAssignedValue<string[]>;
  dates: TaskAssignedValue<Date[]>;
  hour: TaskAssignedValue<Date>;
  notifications: TaskAssignedValue<TaskNotification[]>;
  required?: TaskAssignedValue<CreateTaskAssignBody['task_assign']['mandatory']>;
  canSubmitLate?: TaskAssignedValue<boolean>;
  taskRecurrence?: TaskAssignedValue<CreateTaskAssignBody['task_recurrence']>;
  asset?: TaskAssignedValue<TaskAssignedAsset | null>;
  scheduledSendDaysBefore?: TaskAssignedValue<number | null>;
  /** Day within the parent Planning cycle (cycle-0 template). Used only when the
   * task is being created from the Planning context (?planningId URL param). */
  cycleDay?: TaskAssignedValue<number>;
  /** Time of day (HH:mm) at which the task starts within its cycle day. Only
   * meaningful for non-daily plannings; the daily picker treats `cycleDay` as
   * the hour. Default '09:00'. */
  timeOfDay?: TaskAssignedValue<string>;
}

type ValueChangedAction<K extends keyof TaskAssigned> = {
  type: 'value_changed';
  payload: { key: K; value: TaskAssigned[K]['value'] };
};

type BlurTriggeredAction = {
  type: 'blur_triggered';
  payload: { key: Exclude<keyof TaskAssigned, 'taskResume'> };
};

type TaskDataLoadedAction = {
  type: 'task_data_loaded';
  payload: TaskAssigned;
};

type AddNotificationAction = {
  type: 'add_notification';
  payload: TaskNotification;
};

type RemoveNotificationAction = {
  type: 'remove_notification';
  payload: number;
};

type UpdateNotificationAction = {
  type: 'update_notification';
  payload: {
    index: number;
    key: keyof TaskNotification;
    value: TaskNotification[keyof TaskNotification];
  };
};

export type TaskAssignerActions =
  | ValueChangedAction<keyof TaskAssigned>
  | BlurTriggeredAction
  | TaskDataLoadedAction
  | AddNotificationAction
  | RemoveNotificationAction
  | UpdateNotificationAction;

export const initialState: TaskAssigned = {
  users: {
    value: [],
    showError: false,
    missing: true,
  },
  // `missing: false` because the recipient requirement is satisfied by
  // either `users` OR `groups`. The submit validator checks the disjunction;
  // individual fields shouldn't flag themselves as missing in isolation.
  groups: {
    value: [],
    showError: false,
    missing: false,
  },
  forms: {
    value: [],
    showError: false,
    missing: true,
  },
  location: {
    value: 0,
    showError: false,
    missing: false,
  },
  locationType: { value: 'Location', showError: false, missing: false },
  description: { value: '', showError: false, missing: false },
  dates: { value: [new Date()], showError: false, missing: false },
  hour: { value: new Date(), showError: false, missing: false },
  gap: { value: 4, showError: false, missing: false },
  subTasks: { value: [], showError: false, missing: false },
  notifications: { value: [], showError: false, missing: false },
  required: { value: 2, showError: false, missing: false },
  canSubmitLate: { value: false, showError: false, missing: false },
  taskRecurrence: { value: null, showError: false, missing: false },
  asset: { value: null, showError: false, missing: false },
  scheduledSendDaysBefore: { value: null, showError: false, missing: false },
  cycleDay: { value: 1, showError: false, missing: false },
  timeOfDay: { value: '09:00', showError: false, missing: false },
};
const checkRequiredValues = (key: keyof TaskAssigned, task: TaskAssigned) => {
  if (key === 'users') {
    // Destinatario requirement is `users.length > 0 OR groups.length > 0`.
    // We only flag `users` as missing when BOTH are empty so the form treats
    // a group-only task as valid.
    return task.users.value.length === 0 && task.groups.value.length === 0;
  } else if (key === 'forms' || key === 'dates') {
    return task[key].value.length === 0;
  } else if (key === 'hour') {
    return !task[key].value;
  } else if (key === 'gap') {
    return !task[key].value;
  } else if (key === 'notifications') {
    return task[key].value.some((notif) => !notif.type || !notif.period);
  }
  return false;
};

export const TaskAssignerReducer = (
  state: TaskAssigned,
  action: TaskAssignerActions,
): TaskAssigned => {
  switch (action.type) {
    case 'blur_triggered': {
      const isMissing = checkRequiredValues(action.payload.key, state);
      return {
        ...state,
        [action.payload.key]: {
          ...state[action.payload.key],
          showError: isMissing,
          missing: isMissing,
        },
      };
    }
    case 'value_changed': {
      let newValue = action.payload.value;
      let missing = false;

      if (action.payload.key === 'location' && action.payload.value === state.location.value) {
        newValue = initialState.location.value;
      }

      // Destinatario requirement is satisfied by users OR groups. We need to
      // recompute BOTH fields' missing flag on every change to either, so the
      // submit button reflects the disjunction correctly.
      if (action.payload.key === 'users' || action.payload.key === 'groups') {
        const nextUsers =
          action.payload.key === 'users'
            ? (action.payload.value as number[])
            : state.users.value;
        const nextGroups =
          action.payload.key === 'groups'
            ? (action.payload.value as number[])
            : state.groups.value;
        const bothEmpty = nextUsers.length === 0 && nextGroups.length === 0;
        return {
          ...state,
          users: {
            value: nextUsers,
            showError: false,
            missing: bothEmpty,
          },
          groups: {
            value: nextGroups,
            showError: false,
            missing: false,
          },
        };
      }

      if (action.payload.key === 'forms' && (action.payload.value as number[]).length === 0) {
        missing = true;
      }

      if (action.payload.key === 'dates' && (action.payload.value as Date[]).length === 0) {
        missing = true;
      }

      return {
        ...state,
        [action.payload.key]: {
          value: newValue,
          showError: false,
          missing,
        },
      };
    }
    case 'task_data_loaded': {
      return { ...initialState, ...action.payload };
    }
    case 'add_notification': {
      return {
        ...state,
        notifications: {
          value: [...state.notifications.value, action.payload],
          showError: false,
          missing: false,
        },
      };
    }
    case 'remove_notification': {
      return {
        ...state,
        notifications: {
          value: state.notifications.value.filter((_, i) => i !== action.payload),
          showError: false,
          missing: false,
        },
      };
    }
    case 'update_notification': {
      return {
        ...state,
        notifications: {
          value: state.notifications.value.map((notification, index) =>
            index === action.payload.index
              ? { ...notification, [action.payload.key]: action.payload.value }
              : notification,
          ),
          showError: false,
          missing: false,
        },
      };
    }
    default:
      return state;
  }
};
